import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Eye, EyeOff, LogIn, MessageSquare, Zap, CheckCircle } from 'lucide-react';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const data = await login(email, password);

      if (data.hasPaidRegistration === true) {
        navigate('/dashboard');
      } else {
        navigate('/registration-payment');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-bg">
        <div className="login-bg-gradient"></div>
        <div className="login-bg-pattern"></div>
      </div>
      <div className="login-grid">
        <div className="login-brand">
          <div className="brand-content">
            <div className="brand-logo">
              <div className="logo-icon">J</div>
              <div>
                <div className="logo-name">Jipate</div>
                <div className="logo-tagline">WhatsApp Business Platform</div>
              </div>
            </div>
            <h1 className="brand-title">
              <span className="brand-title-white">WhatsApp Business</span><br />
              <span className="brand-gradient">for Tanzania</span>
            </h1>
            <p className="brand-desc">
              Send bulk messages, manage customers, and grow your business with our powerful WhatsApp platform.
            </p>
            <div className="brand-features">
              <div className="feature-item">
                <div className="feature-dot"></div>
                <span className="feature-text-white">Bulk messaging to opted-in contacts</span>
              </div>
              <div className="feature-item">
                <div className="feature-dot"></div>
                <span className="feature-text-white">AI-powered auto replies</span>
              </div>
              <div className="feature-item">
                <div className="feature-dot"></div>
                <span className="feature-text-white">Product catalog & e-commerce</span>
              </div>
              <div className="feature-item">
                <div className="feature-dot"></div>
                <span className="feature-text-white">Real-time analytics & reports</span>
              </div>
            </div>
            <div className="brand-stats">
              <div className="stat">
                <div className="stat-value">10K+</div>
                <div className="stat-label">Active Businesses</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <div className="stat-value">1M+</div>
                <div className="stat-label">Messages Delivered</div>
              </div>
              <div className="stat-divider"></div>
              <div className="stat">
                <div className="stat-value">99.9%</div>
                <div className="stat-label">Uptime</div>
              </div>
            </div>
          </div>
        </div>

        <div className="login-form-wrapper">
          <div className="login-card animate-slide-up">
            <div className="form-header">
              <div className="form-icon">
                <MessageSquare size={24} />
              </div>
              <h2 className="form-title">Welcome back</h2>
              <p className="form-subtitle">Sign in to your account to continue</p>
            </div>

            {success && (
              <div className="alert-success animate-fade-in">
                <CheckCircle size={16} />
                <span>{success}</span>
              </div>
            )}

            {error && (
              <div className="alert-error animate-fade-in">
                <Zap size={14} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="input-group">
                <label className="input-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Password</label>
                <div className="password-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-options">
                <label className="checkbox-label">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
              </div>

              <button type="submit" className="btn-login" disabled={loading}>
                {loading ? (
                  <span className="btn-loading">
                    <span className="spinner-small"></span>
                    Signing in...
                  </span>
                ) : (
                  <>
                    <LogIn size={18} />
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="form-footer">
              <p>
                Don't have an account? <Link to="/register">Create an account</Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;